import { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UploadSimple, FilePdf, PaperPlaneRight, Eye, X, FileXls, RocketLaunch } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { MfaSettingsCard } from "@/components/MfaSettingsCard";

export default function Settings() {
  const { user } = useAuth();
  const readOnly = user.role !== "admin";
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoCacheBuster, setLogoCacheBuster] = useState(Date.now());
  const fileRef = useRef(null);
  const [testPhone, setTestPhone] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, message }
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewLabel, setPreviewLabel] = useState("");

  const loadSupervisorPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/tools/preview-supervisor-email");
      setPreviewLabel("Supervisor approval email");
      setPreviewHtml(data.html || "");
    } catch (err) {
      toast.error(err.response?.data?.detail?.toString() || err.message);
    } finally { setPreviewLoading(false); }
  };

  const loadWelcomePreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/tools/preview-welcome-email");
      setPreviewLabel("Contractor welcome email");
      setPreviewHtml(data.html || "");
    } catch (err) {
      toast.error(err.response?.data?.detail?.toString() || err.message);
    } finally { setPreviewLoading(false); }
  };

  const loadSupervisorWelcomePreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/tools/preview-supervisor-welcome-email");
      setPreviewLabel("Supervisor welcome email");
      setPreviewHtml(data.html || "");
    } catch (err) {
      toast.error(err.response?.data?.detail?.toString() || err.message);
    } finally { setPreviewLoading(false); }
  };


  useEffect(() => {
    api.get("/settings/company").then((r) => {
      const d = r?.data || {};
      setForm({
        name: d.name || "",
        address_lines: Array.isArray(d.address_lines) ? d.address_lines.join("\n") : "",
        phone: d.phone || "",
        billing_phone: d.billing_phone || "",
        email: d.email || "",
        website: d.website || "",
        tax_id: d.tax_id || "",
        footer_note: d.footer_note || "",
      });
    });
  }, []);

  if (!form) return <div className="p-4 md:p-8">Loading…</div>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        address_lines: form.address_lines.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.put("/settings/company", payload);
      setForm({ ...data, address_lines: (data.address_lines || []).join("\n") });
      toast.success("Company settings saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail?.toString() || err.message);
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/settings/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoCacheBuster(Date.now());
      toast.success("Logo uploaded.");
    } catch (err) {
      toast.error(err.response?.data?.detail?.toString() || err.message);
    }
  };

  const sendTestSms = async () => {
    setTestSending(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/test-sms", { phone: testPhone });
      setTestResult({ ok: true, message: `Sent to ${data.to} from ${data.from} (Twilio message ${data.message_sid?.slice(0, 12)}…). Check your phone within a minute.` });
    } catch (err) {
      setTestResult({ ok: false, message: err.response?.data?.detail?.toString() || err.message });
    } finally { setTestSending(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl">
      <div className="mb-6"><div className="tinylabel">Company</div><h1 className="text-3xl font-black">Settings</h1><p className="text-slate-500 mt-1">Applied to every invoice PDF and outbound email header.</p></div>

      {!readOnly && (
        <Link to="/launch-readiness" data-testid="settings-needs-attention-link"
          className="flex items-center gap-3 bg-white border border-slate-200 rounded-md p-4 mb-6 hover:border-teal-400 hover:shadow-sm transition-colors group">
          <RocketLaunch size={26} weight="duotone" className="text-teal-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-bold text-slate-900">Items that need attention</div>
            <div className="text-xs text-slate-500">One-tap scan for missing data — workers without logins, projects without a supervisor email, clients without a billing email, email/SMS setup.</div>
          </div>
          <span className="text-sm font-semibold text-teal-700 group-hover:underline">Open →</span>
        </Link>
      )}

      {!readOnly && (
        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
          <div className="tinylabel">Onboarding PDFs</div>
          <div className="text-sm text-slate-500 mt-1 mb-4">
            One-page printable guides you can attach to onboarding emails.
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={`${API_BASE}/downloads/worker-quickref`}
              target="_blank"
              rel="noreferrer"
              data-testid="download-worker-quickref-btn"
              className="inline-flex items-center justify-center gap-2 h-11 px-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-md text-sm"
            >
              <FilePdf size={18} /> Download Worker Guide
            </a>
            <a
              href={`${API_BASE}/downloads/supervisor-quickref`}
              target="_blank"
              rel="noreferrer"
              data-testid="download-supervisor-quickref-btn"
              className="inline-flex items-center justify-center gap-2 h-11 px-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-md text-sm"
            >
              <FilePdf size={18} /> Download Supervisor Guide
            </a>
          </div>
        </div>
      )}

      {!readOnly && (
        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6" data-testid="backup-panel">
          <div className="tinylabel">Data backup</div>
          <div className="text-sm text-slate-500 mt-1 mb-4">
            Download every record — workers, clients, projects, assignments, timesheets, invoices,
            payments, pay runs, users — as one Excel workbook (one tab per data type).
            SSN, bank, and ACH details are <b>never</b> included.
            Tip: download one weekly (e.g., every Friday) and keep a few recent copies on your computer.
          </div>
          <a
            href={`${API_BASE}/exports/backup.xlsx`}
            data-testid="download-backup-btn"
            className="inline-flex items-center justify-center gap-2 h-11 px-4 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-md text-sm"
          >
            <FileXls size={18} /> Download full backup (Excel)
          </a>
        </div>
      )}

      {!readOnly && (
        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6" data-testid="email-preview-panel">
          <div className="tinylabel">Email previews</div>
          <div className="text-sm text-slate-500 mt-1 mb-4">
            See exactly what each auto-sent email looks like before real recipients get it — dummy data, no send.
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={loadSupervisorPreview}
              disabled={previewLoading}
              data-testid="preview-supervisor-email-btn"
              className="h-11 px-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Eye size={16} weight="bold" />
              Preview supervisor approval email
            </button>
            <button
              type="button"
              onClick={loadWelcomePreview}
              disabled={previewLoading}
              data-testid="preview-welcome-email-btn"
              className="h-11 px-4 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Eye size={16} weight="bold" />
              Preview contractor welcome email
            </button>
            <button
              type="button"
              onClick={loadSupervisorWelcomePreview}
              disabled={previewLoading}
              data-testid="preview-supervisor-welcome-email-btn"
              className="h-11 px-4 bg-sky-700 hover:bg-sky-800 text-white font-semibold rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Eye size={16} weight="bold" />
              Preview supervisor welcome email
            </button>
          </div>
        </div>
      )}

      {previewHtml !== null && (
        <div
          data-testid="email-preview-modal"
          className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50 overflow-y-auto"
          onClick={() => setPreviewHtml(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-md w-full max-w-3xl border border-slate-200 my-8 max-h-[85vh] flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <div className="tinylabel">Preview</div>
                <div className="font-bold" data-testid="email-preview-label">{previewLabel || "Email"}</div>
              </div>
              <button
                type="button"
                data-testid="close-email-preview"
                onClick={() => setPreviewHtml(null)}
                className="text-slate-500 hover:text-slate-900"
              >
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-slate-50 p-4">
              <iframe
                title="email-preview"
                srcDoc={previewHtml}
                className="w-full h-[70vh] bg-white border border-slate-200 rounded"
                data-testid="email-preview-iframe"
              />
            </div>
          </div>
        </div>
      )}

      {!readOnly && <MfaSettingsCard />}

      {!readOnly && (
        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6" data-testid="test-sms-panel">
          <div className="tinylabel">Twilio SMS diagnostic</div>
          <div className="text-sm text-slate-500 mt-1 mb-4">
            Send a one-off test message to verify Twilio credentials + Messaging
            Service are wired up correctly. Uses your current environment's
            Twilio settings.
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="tel"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="Your cell number (e.g. 7405551234)"
              data-testid="test-sms-phone-input"
              className="flex-1 h-11 px-3 border border-slate-300 rounded-md"
            />
            <button
              type="button"
              onClick={sendTestSms}
              disabled={testSending || !testPhone.trim()}
              data-testid="test-sms-send-btn"
              className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <PaperPlaneRight size={16} weight="bold" />
              {testSending ? "Sending…" : "Send Test SMS"}
            </button>
          </div>
          {testResult && (
            <div
              data-testid={`test-sms-result-${testResult.ok ? "ok" : "err"}`}
              className={`mt-3 border rounded-md p-3 text-sm ${testResult.ok
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-rose-50 border-rose-200 text-rose-800"}`}
            >
              {testResult.ok ? "✅ " : "❌ "}{testResult.message}
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md p-6 mb-6 flex items-center gap-6">
        <img
          src={`${API_BASE}/settings/company/logo?t=${logoCacheBuster}`}
          alt="Company logo"
          className="h-24 w-24 object-contain rounded border border-slate-200 bg-slate-50"
          onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
        />
        <div className="flex-1">
          <div className="tinylabel">Logo</div>
          <div className="text-sm text-slate-500 mt-1">JPEG, PNG or WebP. Square works best. Displayed at ~85px on the invoice.</div>
          {!readOnly && (
            <>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                     className="hidden" data-testid="logo-file-input"
                     onChange={(e) => uploadLogo(e.target.files?.[0])} />
              <button type="button" onClick={() => fileRef.current?.click()}
                      data-testid="upload-logo-btn"
                      className="mt-3 h-10 px-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-md text-sm flex items-center gap-2">
                <UploadSimple size={16} /> Upload new logo
              </button>
            </>
          )}
        </div>
      </div>

      <form onSubmit={save} className="bg-white border border-slate-200 rounded-md p-6 space-y-4" data-testid="company-settings-form">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Company name</label>
          <input value={form.name || ""} disabled={readOnly} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 data-testid="settings-name" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md disabled:bg-slate-50" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Address (one line per line)</label>
          <textarea rows={3} value={form.address_lines || ""} disabled={readOnly}
                    onChange={(e) => setForm({ ...form, address_lines: e.target.value })}
                    data-testid="settings-address" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md disabled:bg-slate-50" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            ["phone", "Main phone"],
            ["billing_phone", "Billing phone"],
            ["email", "Billing email"],
            ["website", "Website"],
            ["tax_id", "Tax ID / EIN"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</label>
              <input value={form[k] || ""} disabled={readOnly} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                     data-testid={`settings-${k}`} className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md disabled:bg-slate-50" />
            </div>
          ))}
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Invoice footer / remittance note</label>
          <input value={form.footer_note || ""} disabled={readOnly} onChange={(e) => setForm({ ...form, footer_note: e.target.value })}
                 data-testid="settings-footer" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md disabled:bg-slate-50" />
        </div>
        {!readOnly && (
          <div className="flex justify-end pt-2">
            <button data-testid="save-settings-btn" disabled={saving} className="h-11 px-5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md disabled:opacity-60">
              {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
